import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Search, Plus, Package, Trash2, LayoutGrid, List, X, Upload, FileText, Image
} from 'lucide-react';
import { toast } from 'sonner';
import { useUserId } from '../context/UserAuthContext';
import OrderDetailModal from '../components/OrderDetailModal';

const API_URL = window.location.origin;

const Orders = () => {
  const userId = useUserId();
  
  const [viewMode, setViewMode] = useState('card');
  const [orders, setOrders] = useState([]);
  const [orderStats, setOrderStats] = useState({ total: 0, quote: 0, shipped: 0, received: 0 });
  const [orderStatusFilter, setOrderStatusFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showNewPurchase, setShowNewPurchase] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const fileInputRef = useRef(null);

  // Fetch orders
  const fetchOrders = useCallback(async () => {
    if (!userId) return;
    try {
      let url = `${API_URL}/api/orders/purchase?user_id=${userId}`;
      if (orderStatusFilter !== 'All') {
        url += `&status=${orderStatusFilter}`;
      }
      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }
      
      const res = await fetch(url);
      const data = await res.json();
      setOrders(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  }, [userId, orderStatusFilter, searchQuery]);

  const fetchOrderStats = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${API_URL}/api/orders/purchase/stats?user_id=${userId}`);
      const data = await res.json();
      setOrderStats(data);
    } catch (error) {
      console.error('Error fetching order stats:', error);
    }
  }, [userId]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchOrders(), fetchOrderStats()]);
      setLoading(false);
    };
    
    if (userId) {
      loadData();
    }
  }, [userId, fetchOrders, fetchOrderStats]);

  // Delete order
  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order?')) return;
    try {
      const res = await fetch(`${API_URL}/api/orders/purchase/${orderId}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        toast.success('Order deleted');
        fetchOrders();
        fetchOrderStats();
      }
    } catch (error) {
      toast.error('Failed to delete order');
    }
  };

  // File upload handling
  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf', 'text/csv'];
    const isCSV = file.name.endsWith('.csv');
    
    if (!allowedTypes.includes(file.type) && !isCSV) {
      toast.error('Unsupported file type. Please upload PDF, CSV, JPG, PNG, or WebP files.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB.');
      return;
    }

    setUploadProgress('reading');
    
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result.split(',')[1];
        
        setUploadProgress('parsing');
        
        try {
          const response = await fetch(`${API_URL}/api/orders/parse-document`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              file_base64: base64,
              file_type: isCSV ? 'text/csv' : file.type,
              file_name: file.name,
              user_id: userId,
              target_type: 'orders'
            })
          });

          const result = await response.json();
          
          if (result.success && result.orders?.length > 0) {
            setParsedData(result.orders);
            setUploadProgress(null);
            toast.success(`Found ${result.orders.length} orders in document`);
          } else {
            setUploadProgress(null);
            toast.error(result.message || 'No orders found in document');
          }
        } catch (error) {
          setUploadProgress(null);
          toast.error('Failed to parse document');
          console.error('Parse error:', error);
        }
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      setUploadProgress(null);
      toast.error('Failed to read file');
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImportParsed = async () => {
    if (!parsedData?.length) return;
    
    setUploadProgress('importing');
    
    try {
      const response = await fetch(`${API_URL}/api/orders/import-parsed?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orders: parsedData,
          bookings: []
        })
      });

      const result = await response.json();
      
      if (result.success) {
        toast.success(`Imported ${result.imported_orders} orders`);
        setParsedData(null);
        setShowUploadModal(false);
        fetchOrders();
        fetchOrderStats();
      } else {
        toast.error('Failed to import orders');
      }
    } catch (error) {
      toast.error('Failed to import orders');
    }
    
    setUploadProgress(null);
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'Paid': return 'bg-green-500 text-white';
      case 'Pending': return 'bg-yellow-500 text-black';
      case 'Billed': return 'bg-blue-500 text-white';
      case 'Partially Paid': return 'bg-orange-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Loading orders...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">Orders</h1>
            <p className="text-gray-400 mt-1">Manage your purchase orders and track shipments</p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* View Toggle */}
            <div className="flex items-center bg-black rounded-lg p-1">
              <button
                onClick={() => setViewMode('card')}
                className={`p-2 rounded ${viewMode === 'card' ? 'bg-white text-black' : 'text-gray-400'}`}
              >
                <LayoutGrid size={18} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-white text-black' : 'text-gray-400'}`}
              >
                <List size={18} />
              </button>
            </div>
            
            {/* Upload Button */}
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-black text-white border border-gray-700 rounded-lg hover:bg-gray-800 transition-colors"
              data-testid="upload-orders-btn"
            >
              <Upload size={20} />
              Upload
            </button>
            
            <button
              onClick={() => setShowNewPurchase(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white text-black rounded-lg hover:bg-gray-100 transition-colors"
              data-testid="new-purchase-btn"
            >
              <Plus size={20} />
              New Purchase
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex gap-6">
          {/* Sidebar Filters */}
          <div className="w-64 flex-shrink-0 space-y-6">
            {/* Purchase Status */}
            <div className="bg-black border border-gray-400 rounded-xl hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 p-4">
              <h3 className="text-xs font-semibold text-gray-400 mb-3">PURCHASE STATUS</h3>
              <div className="space-y-2">
                {[
                  { label: 'All', count: orderStats.total },
                  { label: 'Quote', count: orderStats.quote },
                  { label: 'Shipped', count: orderStats.shipped },
                  { label: 'Received', count: orderStats.received }
                ].map(item => (
                  <button
                    key={item.label}
                    onClick={() => setOrderStatusFilter(item.label)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                      orderStatusFilter === item.label 
                        ? 'bg-white text-black' 
                        : 'text-gray-300 hover:bg-gray-800'
                    }`}
                  >
                    <span>{item.label}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      orderStatusFilter === item.label ? 'bg-black text-white' : 'bg-gray-700'
                    }`}>
                      {item.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Search */}
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search orders..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-black border border-gray-400 rounded-lg text-white text-sm focus:outline-none focus:border-gray-600"
              />
            </div>
          </div>

          {/* Orders Grid */}
          <div className="flex-1">
            {orders.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <Package size={48} className="mx-auto mb-4 opacity-50" />
                <p>No orders found</p>
              </div>
            ) : (
              <div className={viewMode === 'card' ? 'grid grid-cols-1 lg:grid-cols-2 gap-4' : 'space-y-3'}>
                {orders.map(order => (
                  <div 
                    key={order.id}
                    className="bg-black border border-gray-400 rounded-xl p-4 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <span className="text-xs text-gray-500">{order.po_number}</span>
                        <h3 className="text-white font-medium">{order.items?.[0]?.product_name || 'Unknown Product'}</h3>
                        <p className="text-gray-400 text-sm">Qty: {order.items?.[0]?.quantity || 0}</p>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getPaymentStatusColor(order.payment_status)}`}>
                        {order.payment_status}
                      </span>
                    </div>

                    <div className="mb-4">
                      <p className="text-sm text-white">{order.vendor_name}</p>
                      <p className="text-xs text-gray-500">{order.vendor_address}</p>
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between text-xs mb-2">
                        <span className={order.status === 'Quote' ? 'text-blue-400' : 'text-gray-500'}>Order placed</span>
                        <span className={order.status === 'Shipped' ? 'text-blue-400' : 'text-gray-500'}>Shipped</span>
                        <span className={order.status === 'Received' ? 'text-blue-400' : 'text-gray-500'}>Delivered</span>
                      </div>
                      <div className="h-1 bg-gray-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-blue-500 transition-all"
                          style={{
                            width: order.status === 'Quote' ? '33%' : order.status === 'Shipped' ? '66%' : '100%'
                          }}
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-xl font-bold text-white">${order.total_amount?.toLocaleString()}</span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedOrder({
                              id: order.po_number,
                              product: order.items?.[0]?.product_name,
                              price: `$${order.total_amount}`,
                              date: order.order_date,
                              payment: order.payment_status,
                              address: order.vendor_address
                            });
                            setShowOrderModal(true);
                          }}
                          className="px-3 py-1.5 bg-black border border-gray-700 text-white rounded text-sm hover:bg-gray-900"
                        >
                          Track shipment
                        </button>
                        <button
                          onClick={() => handleDeleteOrder(order.id)}
                          className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-gray-800 rounded"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Order Detail Modal */}
        <OrderDetailModal 
          isOpen={showOrderModal} 
          onClose={() => { setShowOrderModal(false); setSelectedOrder(null); }}
          order={selectedOrder}
        />
        
        {/* New Purchase Modal */}
        {showNewPurchase && (
          <NewPurchaseModal
            userId={userId}
            onClose={() => setShowNewPurchase(false)}
            onCreated={() => {
              setShowNewPurchase(false);
              fetchOrders();
              fetchOrderStats();
            }}
          />
        )}
        
        {/* Upload Modal */}
        {showUploadModal && (
          <div 
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => { setShowUploadModal(false); setParsedData(null); setUploadProgress(null); }}
            data-testid="upload-modal"
          >
            <div 
              className="bg-black border border-gray-400 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
                <h3 className="text-white text-lg font-semibold">Upload Orders</h3>
                <button 
                  onClick={() => { setShowUploadModal(false); setParsedData(null); setUploadProgress(null); }} 
                  className="text-gray-400 hover:text-white"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="px-6 py-5 space-y-5">
                {/* Upload Area */}
                {!parsedData && (
                  <div className="space-y-4">
                    <p className="text-gray-400 text-sm">
                      Upload a PDF, CSV, or image file containing order data. The system will automatically extract order information.
                    </p>
                    
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      accept=".pdf,.csv,.jpg,.jpeg,.png,.webp"
                      className="hidden"
                      data-testid="file-input"
                    />
                    
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-gray-700 rounded-xl p-8 text-center cursor-pointer hover:border-gray-500 transition-colors"
                    >
                      {uploadProgress ? (
                        <div className="flex flex-col items-center gap-3">
                          <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          <p className="text-white">
                            {uploadProgress === 'reading' ? 'Reading file...' : 
                             uploadProgress === 'parsing' ? 'Extracting orders with AI...' : 
                             'Processing...'}
                          </p>
                        </div>
                      ) : (
                        <>
                          <Upload size={40} className="mx-auto text-gray-500 mb-3" />
                          <p className="text-white font-medium">Click to upload or drag and drop</p>
                          <p className="text-gray-500 text-sm mt-1">PDF, CSV, JPG, PNG, WebP (max 10MB)</p>
                        </>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <div className="flex items-center gap-2">
                        <FileText size={16} />
                        <span>CSV/PDF invoices</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Image size={16} />
                        <span>Screenshots of orders</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Parsed Data Preview */}
                {parsedData && parsedData.length > 0 && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-white font-medium">Found {parsedData.length} Orders</h4>
                      <button
                        onClick={() => { setParsedData(null); }}
                        className="text-gray-400 hover:text-white text-sm"
                      >
                        Upload different file
                      </button>
                    </div>
                    
                    <div className="space-y-3 max-h-[300px] overflow-y-auto">
                      {parsedData.map((order, idx) => (
                        <div key={idx} className="bg-black border border-gray-700 rounded-lg p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="text-white font-medium">{order.vendor_name || 'Unknown Vendor'}</p>
                              <p className="text-gray-400 text-sm">{order.vendor_address}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-white font-medium">${(order.total_amount || 0).toLocaleString()}</p>
                              <p className="text-gray-500 text-xs">{order.order_date}</p>
                            </div>
                          </div>
                          {order.items?.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-gray-800">
                              {order.items.map((item, iIdx) => (
                                <p key={iIdx} className="text-gray-400 text-sm">
                                  {item.product_name} × {item.quantity}
                                </p>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-800">
                      <button
                        onClick={() => { setShowUploadModal(false); setParsedData(null); }}
                        className="px-4 py-2 text-gray-300 hover:text-white"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleImportParsed}
                        disabled={uploadProgress === 'importing'}
                        className="px-5 py-2 bg-white text-black rounded-lg font-medium hover:bg-gray-100 disabled:opacity-50"
                        data-testid="import-orders-btn"
                      >
                        {uploadProgress === 'importing' ? 'Importing...' : `Import ${parsedData.length} Orders`}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// New Purchase Modal — creates a purchase order via POST /api/orders/purchase
const NewPurchaseModal = ({ userId, onClose, onCreated }) => {
  const [vendorName, setVendorName] = useState('');
  const [vendorAddress, setVendorAddress] = useState('');
  const [status, setStatus] = useState('Quote');
  const [paymentStatus, setPaymentStatus] = useState('Pending');
  const [expectedDelivery, setExpectedDelivery] = useState('');
  const [items, setItems] = useState([
    { product_name: '', quantity: 1, unit_price: 0 }
  ]);
  const [submitting, setSubmitting] = useState(false);

  const updateItem = (idx, field, value) => {
    setItems(prev => prev.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  };

  const addItemRow = () => {
    setItems(prev => [...prev, { product_name: '', quantity: 1, unit_price: 0 }]);
  };

  const removeItemRow = (idx) => {
    setItems(prev => prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx));
  };

  const computedItems = items.map(it => ({
    product_name: it.product_name.trim(),
    quantity: Number(it.quantity) || 0,
    unit_price: Number(it.unit_price) || 0,
    total_price: (Number(it.quantity) || 0) * (Number(it.unit_price) || 0),
  }));
  const totalAmount = computedItems.reduce((sum, it) => sum + it.total_price, 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!vendorName.trim()) {
      toast.error('Vendor name is required');
      return;
    }
    const validItems = computedItems.filter(it => it.product_name && it.quantity > 0);
    if (validItems.length === 0) {
      toast.error('Please add at least one item with a name and quantity');
      return;
    }
    
    setSubmitting(true);
    try {
      const body = {
        vendor_name: vendorName.trim(),
        vendor_address: vendorAddress.trim(),
        items: validItems,
        total_amount: validItems.reduce((s, it) => s + it.total_price, 0),
        status,
        payment_status: paymentStatus,
        expected_delivery: expectedDelivery || null,
        user_id: userId,
      };
      const res = await fetch(`${API_URL}/api/orders/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(`Purchase ${data.po_number} created`);
        onCreated();
      } else {
        toast.error(data.detail || 'Failed to create purchase');
      }
    } catch (err) {
      toast.error('Failed to create purchase');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="new-purchase-modal"
    >
      <div 
        className="bg-black border border-gray-400 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800 sticky top-0 bg-black z-10">
          <h3 className="text-white text-lg font-semibold">New Purchase</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="close-new-purchase">
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
          {/* Vendor */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Vendor Name *</label>
              <input
                type="text"
                value={vendorName}
                onChange={e => setVendorName(e.target.value)}
                className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-gray-500"
                placeholder="e.g. Acme Supplies"
                required
                data-testid="vendor-name-input"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Vendor Address</label>
              <input
                type="text"
                value={vendorAddress}
                onChange={e => setVendorAddress(e.target.value)}
                className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-gray-500"
                placeholder="e.g. 123 Market St, SF"
                data-testid="vendor-address-input"
              />
            </div>
          </div>

          {/* Status */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Status</label>
              <select
                value={status}
                onChange={e => setStatus(e.target.value)}
                className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm"
                data-testid="status-select"
              >
                <option>Quote</option>
                <option>Shipped</option>
                <option>Received</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Payment</label>
              <select
                value={paymentStatus}
                onChange={e => setPaymentStatus(e.target.value)}
                className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm"
                data-testid="payment-select"
              >
                <option>Pending</option>
                <option>Paid</option>
                <option>Billed</option>
                <option>Partially Paid</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Expected Delivery</label>
              <input
                type="date"
                value={expectedDelivery}
                onChange={e => setExpectedDelivery(e.target.value)}
                className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm"
                data-testid="delivery-input"
              />
            </div>
          </div>

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs text-gray-400 uppercase tracking-wide">Items</label>
              <button
                type="button"
                onClick={addItemRow}
                className="text-xs text-white/70 hover:text-white flex items-center gap-1"
                data-testid="add-item-row"
              >
                <Plus size={14} /> Add item
              </button>
            </div>
            <div className="space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                  <input
                    type="text"
                    value={it.product_name}
                    onChange={e => updateItem(idx, 'product_name', e.target.value)}
                    placeholder="Product name"
                    className="col-span-6 px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm"
                    data-testid={`item-name-${idx}`}
                  />
                  <input
                    type="number"
                    min="1"
                    value={it.quantity}
                    onChange={e => updateItem(idx, 'quantity', e.target.value)}
                    placeholder="Qty"
                    className="col-span-2 px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm"
                    data-testid={`item-qty-${idx}`}
                  />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={it.unit_price}
                    onChange={e => updateItem(idx, 'unit_price', e.target.value)}
                    placeholder="Unit price"
                    className="col-span-3 px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm"
                    data-testid={`item-price-${idx}`}
                  />
                  <button
                    type="button"
                    onClick={() => removeItemRow(idx)}
                    disabled={items.length <= 1}
                    className="col-span-1 text-gray-400 hover:text-red-400 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-3 text-sm text-gray-300">
              Total: <span className="ml-2 font-semibold text-white">${totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2 border-t border-gray-800">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-300 hover:text-white"
              data-testid="cancel-new-purchase"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-5 py-2 bg-white text-black rounded-lg font-medium hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-new-purchase"
            >
              {submitting ? 'Creating…' : 'Create Purchase'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Orders;
